import {Component, forwardRef, Input, OnChanges, SimpleChanges} from "@angular/core";
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {PaginationItemModel} from "../../../models/components/form-controls/pagination/pagination-item.model";
import {PaginationOptionsModel} from "../../../models/components/form-controls/pagination/pagination-options.model";
import {PaginationModel} from "../../../models/components/form-controls/pagination/pagination.model";
import {WindowRefService} from "../../../services/window-ref.service";
import {FormControlBaseComponent} from "../form-control-base/form-control-base.component";

@Component({
    selector: "pcg-ng-pagination",
    template: require('./pagination.component.html'),
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => PaginationComponent), multi: true},
    ]
})
export class PaginationComponent extends FormControlBaseComponent<number> implements OnChanges {
    private options: PaginationOptionsModel = new PaginationOptionsModel();

    @Input()
    public pagination: PaginationModel;

    public items: Array<PaginationItemModel> = new Array<PaginationItemModel>();

    public constructor(private windowRef: WindowRefService) {
        super(/*DEFAULT_VALUE*/1);
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const pagination = changes.pagination.currentValue as PaginationItemModel;
        if (pagination) {
            if(this.controlValue !== pagination.pageNumber) {
                this.writeNewValue(pagination.pageNumber);
            }
            this.calculatePaginationItems();
        }
    }

    public handlePageNumberClick(pageNumber: number): boolean {
        this.writeNewValue(pageNumber);
        return false;
    }

    private calculatePaginationItems(): void {
        this.items = [];

        const pagination = this.pagination;

        if (pagination.pageNumber > 1 && pagination.pageNumber <= pagination.totalPages) {
            this.addPaginationItem(pagination.pageNumber - 1, this.options.labelPrevious);
        }

        if (pagination.totalPages <= this.options.buttonsCount) {
            for (let i = 1; i <= pagination.totalPages; i++) {
                this.addPaginationItem(i, i);
            }
        } else {
            const maxIndex = pagination.totalPages - this.options.buttonsCount;
            let start = Math.max(pagination.pageNumber - Math.ceil(this.options.buttonsCount / 2), 0);
            start = Math.min(maxIndex, start);

            if (start > 0) {
                this.addPaginationItem(1, this.options.labelFirst);
                this.addPaginationItem(-1, this.options.labelSkip);
            }

            for (let i = start + 1; i <= this.options.buttonsCount + start; i++) {
                this.addPaginationItem(i, i);
            }

            if (start < maxIndex) {
                this.addPaginationItem(-1, this.options.labelSkip);
                this.addPaginationItem(pagination.totalPages, this.options.labelLast);
            }
        }

        if (pagination.pageNumber < pagination.totalPages) {
            this.addPaginationItem(pagination.pageNumber + 1, this.options.labelNext);
        }
    }

    private addPaginationItem(pageNumber: number, displayName: number | string): void {
        if (displayName) {
            const url = pageNumber > 0
                ? this.generatePaginationQuery(pageNumber, this.options.queryKey)
                : "";

            const isCurrent = (this.controlValue === pageNumber) && (displayName === pageNumber);

            const paginationItem = new PaginationItemModel(pageNumber, String(displayName || pageNumber), url, isCurrent);

            this.items.push(paginationItem);
        }
    }

    public generatePaginationQuery(pageIndex: number, queryKey: string): string {
        const currentQueryString = (WindowRefService.getQueryString() || "");
        const queryPairs = currentQueryString.split("&");

        const query = queryPairs
            .filter(param => param.split("=")[0] !== queryKey)
            .concat([`${queryKey}=${pageIndex}`])
            .join("&");

        return `?${query}`;
    }
}
